import { useEffect, useState } from 'react';
import { Save, Clock, Users, Shuffle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { getRotation, updateRotation, getManagers } from '@/lib/api';

const DAYS = [
  { id: 0, label: 'Пн' },
  { id: 1, label: 'Вт' },
  { id: 2, label: 'Ср' },
  { id: 3, label: 'Чт' },
  { id: 4, label: 'Пт' },
  { id: 5, label: 'Сб' },
  { id: 6, label: 'Вс' },
];

const TIMEZONES = [
  { value: 'Europe/Moscow', label: 'Москва (UTC+3)' },
  { value: 'Europe/Kiev', label: 'Киев (UTC+2)' },
  { value: 'Asia/Tashkent', label: 'Ташкент (UTC+5)' },
  { value: 'Asia/Almaty', label: 'Алматы (UTC+6)' },
  { value: 'Europe/London', label: 'Лондон (UTC+0)' },
  { value: 'Europe/Berlin', label: 'Берлин (UTC+1)' },
  { value: 'UTC', label: 'UTC' },
];

export default function AdminRotation() {
  const [settings, setSettings] = useState(null);
  const [managers, setManagers] = useState([]);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rotationRes, managersRes] = await Promise.all([
        getRotation(),
        getManagers()
      ]);
      const activeManagers = managersRes.data.filter(m => m.is_active);
      setManagers(activeManagers);
      
      const rotationData = rotationRes.data;
      if (!rotationData.round_robin_managers || rotationData.round_robin_managers.length === 0) {
        rotationData.round_robin_managers = activeManagers.map(m => m.id);
      }
      if (!rotationData.timezone) {
        rotationData.timezone = 'Europe/Moscow';
      }
      setSettings(rotationData);
    } catch (err) {
      toast.error('Не удалось загрузить данные');
    } finally {
      setBusy(false);
    }
  };

  const persistDraft = async () => {
    setSaving(true);
    try {
      await updateRotation(settings);
      toast.success('Сохранено! Ротация обновлена.');
    } catch (err) {
      toast.error('Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  const addScheduleSlot = () => {
    setSettings({
      ...settings,
      schedule_slots: [
        ...(settings.schedule_slots || []),
        {
          start_time: '09:00',
          end_time: '18:00',
          manager_id: managers[0]?.id || '',
          days: [0, 1, 2, 3, 4, 5, 6]
        }
      ]
    });
  };

  const updateScheduleSlot = (index, field, value) => {
    const slots = [...settings.schedule_slots];
    slots[index] = { ...slots[index], [field]: value };
    setSettings({ ...settings, schedule_slots: slots });
  };

  const toggleSlotDay = (slotIndex, dayId) => {
    const slots = [...settings.schedule_slots];
    const days = slots[slotIndex].days || [];
    if (days.includes(dayId)) {
      slots[slotIndex].days = days.filter(d => d !== dayId);
    } else {
      slots[slotIndex].days = [...days, dayId].sort();
    }
    setSettings({ ...settings, schedule_slots: slots });
  };

  const removeScheduleSlot = (index) => {
    setSettings({
      ...settings,
      schedule_slots: settings.schedule_slots.filter((_, i) => i !== index)
    });
  };

  const toggleRoundRobinManager = (managerId) => {
    const list = settings.round_robin_managers || [];
    if (list.includes(managerId)) {
      setSettings({
        ...settings,
        round_robin_managers: list.filter(id => id !== managerId)
      });
    } else {
      setSettings({
        ...settings,
        round_robin_managers: [...list, managerId]
      });
    }
  };

  if (busy) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!settings) return null;

  const selectedRRManagers = managers.filter(m => settings.round_robin_managers?.includes(m.id));

  return (
    <div className="space-y-6" data-testid="admin-rotation">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ротация менеджеров</h1>
          <p className="text-slate-500">Настройка распределения лидов</p>
        </div>
        <Button onClick={persistDraft} disabled={saving} data-testid="save-rotation-button">
          {saving ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Сохранение...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              Сохранить
            </span>
          )}
        </Button>
      </div>

      {/* Timezone */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Label className="min-w-fit">Часовой пояс:</Label>
            <Select
              value={settings.timezone || 'Europe/Moscow'}
              onValueChange={(value) => setSettings({ ...settings, timezone: value })}
            >
              <SelectTrigger className="w-64" data-testid="timezone-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Mode Selection */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card
          className={`cursor-pointer transition-all ${settings.mode === 'schedule' ? 'ring-2 ring-slate-900' : ''}`}
          onClick={() => setSettings({ ...settings, mode: 'schedule' })}
          data-testid="mode-schedule"
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h3 className="font-semibold">По расписанию</h3>
                <p className="text-sm text-slate-500">По времени</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${settings.mode === 'round_robin' ? 'ring-2 ring-slate-900' : ''}`}
          onClick={() => setSettings({ ...settings, mode: 'round_robin' })}
          data-testid="mode-round-robin"
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <h3 className="font-semibold">По кругу</h3>
                <p className="text-sm text-slate-500">Каждые N минут</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className={`cursor-pointer transition-all ${settings.mode === 'random' ? 'ring-2 ring-slate-900' : ''}`}
          onClick={() => setSettings({ ...settings, mode: 'random' })}
          data-testid="mode-random"
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Shuffle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">Случайный</h3>
                <p className="text-sm text-slate-500">Рандом</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Schedule Mode */}
      {settings.mode === 'schedule' && (
        <Card>
          <CardHeader>
            <CardTitle>Расписание</CardTitle>
            <CardDescription>Менеджер показывается в указанное время (по вашему часовому поясу)</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {settings.schedule_slots?.map((slot, index) => (
              <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="space-y-1">
                    <Label className="text-xs">С</Label>
                    <Input
                      type="time"
                      value={slot.start_time}
                      onChange={(e) => updateScheduleSlot(index, 'start_time', e.target.value)}
                      className="w-28"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">До</Label>
                    <Input
                      type="time"
                      value={slot.end_time}
                      onChange={(e) => updateScheduleSlot(index, 'end_time', e.target.value)}
                      className="w-28"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px] space-y-1">
                    <Label className="text-xs">Менеджер</Label>
                    <Select
                      value={slot.manager_id}
                      onValueChange={(value) => updateScheduleSlot(index, 'manager_id', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите" />
                      </SelectTrigger>
                      <SelectContent>
                        {managers.map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeScheduleSlot(index)}
                    className="text-red-600 hover:text-red-700 mt-5"
                  >
                    Удалить
                  </Button>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-xs mr-2">Дни:</Label>
                  {DAYS.map((day) => (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => toggleSlotDay(index, day.id)}
                      className={`w-9 h-9 text-xs rounded-lg transition-all ${
                        slot.days?.includes(day.id)
                          ? 'bg-slate-900 text-white'
                          : 'bg-white border border-slate-200 text-slate-600'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <Button variant="outline" onClick={addScheduleSlot}>
              + Добавить слот
            </Button>

            {(!settings.schedule_slots || settings.schedule_slots.length === 0) && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Добавьте слоты. Без них будет показан первый активный менеджер.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Round Robin Mode */}
      {settings.mode === 'round_robin' && (
        <Card>
          <CardHeader>
            <CardTitle>По кругу</CardTitle>
            <CardDescription>Менеджеры меняются автоматически каждые N минут</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Интервал смены</Label>
              <Select
                value={String(settings.interval_minutes)}
                onValueChange={(value) => setSettings({ ...settings, interval_minutes: parseInt(value) })}
              >
                <SelectTrigger className="w-40" data-testid="interval-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 минута</SelectItem>
                  <SelectItem value="2">2 минуты</SelectItem>
                  <SelectItem value="5">5 минут</SelectItem>
                  <SelectItem value="10">10 минут</SelectItem>
                  <SelectItem value="15">15 минут</SelectItem>
                  <SelectItem value="20">20 минут</SelectItem>
                  <SelectItem value="30">30 минут</SelectItem>
                  <SelectItem value="60">1 час</SelectItem>
                  <SelectItem value="120">2 часа</SelectItem>
                  <SelectItem value="240">4 часа</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Менеджеры в ротации</Label>
              <div className="space-y-2">
                {managers.map((manager) => (
                  <div
                    key={manager.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${
                      settings.round_robin_managers?.includes(manager.id) ? 'bg-sky-50' : 'bg-slate-50'
                    }`}
                    onClick={() => toggleRoundRobinManager(manager.id)}
                  >
                    <Checkbox
                      checked={settings.round_robin_managers?.includes(manager.id)}
                      onCheckedChange={() => toggleRoundRobinManager(manager.id)}
                    />
                    <span className="font-medium">{manager.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {selectedRRManagers.length > 0 && (
              <Alert>
                <AlertDescription>
                  Выбрано: {selectedRRManagers.map(m => m.name).join(' → ')}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2 pt-4 border-t">
              <Label>Закрепление (0 = выкл)</Label>
              <p className="text-sm text-slate-500">Пользователь видит одного менеджера N минут</p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={settings.session_lock_minutes}
                  onChange={(e) => setSettings({ ...settings, session_lock_minutes: parseInt(e.target.value) || 0 })}
                  className="w-24"
                />
                <span className="text-slate-500">мин</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Random Mode */}
      {settings.mode === 'random' && (
        <Card>
          <CardHeader>
            <CardTitle>Случайный</CardTitle>
            <CardDescription>Каждому посетителю случайный менеджер</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                settings.balance_by_clicks ? 'bg-green-50 border-green-300' : 'bg-slate-50 border-slate-200'
              }`}
              onClick={() =>
                setSettings({ ...settings, balance_by_clicks: !settings.balance_by_clicks })
              }
              data-testid="balance-by-clicks-toggle"
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={!!settings.balance_by_clicks}
                  onCheckedChange={() =>
                    setSettings({
                      ...settings,
                      balance_by_clicks: !settings.balance_by_clicks,
                    })
                  }
                />
                <div className="flex-1">
                  <div className="font-medium">Балансировка по кликам (умный рандом)</div>
                  <p className="text-sm text-slate-600 mt-1">
                    Каждому новому посетителю показывается менеджер с наименьшим числом кликов
                    за сегодня (с 00:00 по Киеву). Счётчик обнуляется каждую полночь — распределение
                    начинается с нуля каждый день, вчерашние клики не учитываются.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-4 border-t">
              <Label>Закрепление (0 = выкл)</Label>
              <p className="text-sm text-slate-500">Чтобы один пользователь видел одного менеджера</p>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={settings.session_lock_minutes}
                  onChange={(e) => setSettings({ ...settings, session_lock_minutes: parseInt(e.target.value) || 0 })}
                  className="w-24"
                />
                <span className="text-slate-500">мин</span>
              </div>
            </div>
            <Alert>
              <AlertDescription>
                Активных менеджеров: {managers.length}
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
